import { useEffect, useState } from 'react'

/** Ignore sub-pixel and momentum jitter; only a deliberate swipe flips direction. */
const MIN_DELTA_PX = 8

/**
 * `true` while the page is being scrolled down past `threshold`, back to
 * `false` on any upward scroll or once near the top again.
 *
 * Drives viewport-docked affordances (the floating assistant) that would
 * otherwise sit permanently over whatever content occupies the bottom-right
 * of a phone viewport — on a form column that is an input the user must tap.
 */
export function useScrollingDown(threshold = 80): boolean {
  const [scrollingDown, setScrollingDown] = useState(false)

  useEffect(() => {
    let lastY = window.scrollY
    let frame = 0
    const onScroll = (): void => {
      if (frame !== 0) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const y = window.scrollY
        const delta = y - lastY
        // Leave `lastY` untouched below the threshold so slow drags still
        // accumulate into a direction instead of being discarded frame by frame.
        if (Math.abs(delta) < MIN_DELTA_PX) return
        lastY = y
        setScrollingDown(delta > 0 && y > threshold)
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [threshold])

  return scrollingDown
}
